/**
 * The AI "TL;DR" card under a thread's root (docs/features/thread-summaries.md; rules in logic/threadSummary).
 * Follows the thread's root (its `summary` arrives with `message.updated`), asks the server for a summary on
 * Summarize / Refresh, and remembers the collapsed state per workspace. Hidden when the server has no model or the
 * workspace turned summaries off.
 */
import type { Message } from "@bundle/contracts";
import { collapsedKey, REQUEST_TIMEOUT_MS, summaryCard, type SummaryCard } from "../logic/threadSummary.ts";
import { ViewModel } from "./ViewModel.ts";
import type { ChannelViewModel } from "./ChannelViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type ThreadSummaryState = { card: SummaryCard };

/** The "updated N min ago" line is recomputed this often while the card is shown. */
const TICK_MS = 60_000;

export class ThreadSummaryViewModel extends ViewModel<ThreadSummaryState> {
  private requestedAt: number | undefined;
  private failed = false;
  private collapsed: boolean;
  private cancelTimeout: (() => void) | undefined;
  private cancelTick: (() => void) | undefined;

  constructor(
    private readonly ws: WorkspaceViewModel,
    private readonly thread: ChannelViewModel,
    private readonly rootId: string,
  ) {
    super({ card: { kind: "hidden" } });
    this.collapsed = ws.deps.storage.get(collapsedKey(ws.wsId)) === "1";
    this.onDispose(ws.subscribe(() => this.derive()));
    this.onDispose(thread.subscribe(() => this.derive()));
    this.onDispose(() => {
      this.cancelTimeout?.();
      this.cancelTick?.();
    });
    this.derive();
  }

  private root(): Message | undefined {
    const s = this.thread.state;
    if (s.root?.id === this.rootId) return s.root;
    for (const i of s.items) if (i.type === "message" && i.message.id === this.rootId) return i.message;
    return undefined;
  }

  private derive(): void {
    const cache = this.ws.state.cache;
    const root = this.root();
    const now = this.ws.deps.clock.now();
    if (root?.summary !== undefined && this.requestedAt !== undefined && root.summary.generatedAt >= this.requestedAt) this.settle(false);
    const card = summaryCard({
      available: cache?.aiAvailable === true,
      enabled: cache?.workspace.aiThreadSummaries !== false,
      replyCount: root?.replyCount ?? 0,
      summary: root?.summary,
      requestedAt: this.requestedAt,
      failed: this.failed,
      collapsed: this.collapsed,
      now,
    });
    if (card.kind === "ready" && this.cancelTick === undefined) {
      this.cancelTick = this.ws.deps.clock.setTimer(TICK_MS, () => {
        this.cancelTick = undefined;
        this.derive();
      });
    }
    if (JSON.stringify(card) !== JSON.stringify(this.state.card)) this.setState({ card });
  }

  private settle(failed: boolean): void {
    this.requestedAt = undefined;
    this.failed = failed;
    this.cancelTimeout?.();
    this.cancelTimeout = undefined;
  }

  /** Summarize / Refresh summary: the server runs it in the background; the summary arrives on the root. */
  async summarize(): Promise<void> {
    const card = this.state.card;
    if (card.kind === "hidden" || card.loading) return;
    const at = this.ws.deps.clock.now();
    this.requestedAt = at;
    this.failed = false;
    this.cancelTimeout?.();
    this.cancelTimeout = this.ws.deps.clock.setTimer(REQUEST_TIMEOUT_MS, () => {
      this.cancelTimeout = undefined;
      if (this.requestedAt === at) {
        this.settle(true);
        this.derive();
      }
    });
    this.derive();
    try {
      const res = await this.ws.deps.api.summarizeThread(this.ws.wsId, this.rootId);
      if (this.isDisposed) return;
      // Nothing changed since the current summary: there is nothing to wait for.
      if (res.status === "unchanged" && this.requestedAt === at) this.settle(this.root()?.summary === undefined);
    } catch {
      if (this.isDisposed) return;
      if (this.requestedAt === at) this.settle(true);
    }
    this.derive();
  }

  /** Collapses or expands the card (remembered for every thread of this workspace). */
  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
    if (this.collapsed) this.ws.deps.storage.set(collapsedKey(this.ws.wsId), "1");
    else this.ws.deps.storage.remove(collapsedKey(this.ws.wsId));
    this.derive();
  }
}
